<script setup lang="ts">
import type { Component, Document, Page } from '@bloomreach/spa-sdk'
import { toRefs } from 'vue'
import type { TRefPage } from '@/components/PlusBlog/type'

interface IPostItem {
    mainImage: TRefPage
    alt: string
    categories: string[]
    publicationdate: string
    title: string
    url: string
}
/**
 * SDX
 */

const props = defineProps<{
    component: Component
    page: Page
}>()

const { component, page } = toRefs(props)

const documentRef = computed(() => component.value.getModels().document)
const documentPage = computed(() => documentRef.value && page.value.getContent<Document>(documentRef.value))
const data = computed(() => documentPage.value?.getData())
const { blogposts } = data?.value || []

const arrayBlogPostRecommendedToShow = computed(() => {
    return blogposts?.map((element: IPostItem) => {
        const { categories, publicationdate, mainImage, title, url } = element
        const documentPostImg = mainImage?.$ref && page.value.getContent<Document>(mainImage.$ref)
        const postImg = useImageRef(documentPostImg)

        return {
            url,
            src: postImg?.value?.src,
            alt: postImg?.value?.alt || documentPostImg?.model?.data.altText,
            categories,
            publicationdate,
            title,
        }
    })
})

const specClass = [
    'md:col-span-2 lg:col-span-2 lg:row-span-2col-span-1 row-span-2 md:col-span-2 lg:col-span-2',
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
]
</script>

<template>
    <section class="mx-auto max-w-6.5xl px-3 pb-4 xl:px-0">
        <div
            class="grid grid-cols-1 grid-rows-[repeat(4,minmax(0,1fr))] gap-4 md:grid-cols-2 md:grid-rows-[repeat(3,minmax(0,1fr))] lg:grid-cols-3 lg:grid-rows-[repeat(2,minmax(0,1fr))]"
        >
            <div
                v-for="(el, idx) in arrayBlogPostRecommendedToShow"
                :key="idx"
                class="group"
                :class="[specClass[idx]]"
            >
                <div
                    class="rounded-lg bg-plus-primary text-white"
                    :class="[idx === 0 ? '' : 'flex h-35 md:block md:h-81 lg:h-full lg:max-h-[324px]']"
                >
                    <div
                        class="overflow-hidden"
                        :class="[
                            idx === 0
                                ? ' h-[217px] w-full rounded-t-lg md:h-[460px] '
                                : 'h-full w-5/12 rounded-l-lg object-cover md:h-[208px] md:w-full md:rounded-none md:!rounded-t-lg',
                        ]"
                    >
                        <a :href="el.url">
                            <AtomImage
                                :src="el.src"
                                :alt="el.alt"
                                :img-class="[
                                    'w-full transition-transform duration-200 ease-out group-hover:scale-104 object-cover',
                                    idx === 0 ? 'h-[217px] md:h-[460px]' : 'h-full ',
                                ]"
                            />
                        </a>
                    </div>
                    <div
                        class="text-white"
                        :class="[
                            idx === 0
                                ? 'h-[100px] p-4 md:h-[204px] md:p-10'
                                : 'flex w-7/12 flex-col justify-between p-3 md:w-full md:p-6',
                        ]"
                    >
                        <a
                            :href="el.url"
                            class="flex justify-between text-plus-gray-200"
                            :class="[idx === 0 ? 'text-xs md:text-base' : 'text-xxs md:text-xs']"
                        >
                            <p class="font-normal">
                                {{ el.categories?.join(', ') || '' }}
                            </p>
                            <p
                                class="font-light"
                                :class="{ 'hidden md:block': idx !== 0 }"
                            >
                                {{ el.publicationdate }}
                            </p>
                        </a>
                        <a :href="el.url">
                            <h2
                                class="font-bold text-white"
                                :class="[
                                    idx === 0
                                        ? 'mt-2 line-clamp-2 text-base md:text-2md md:leading-10'
                                        : 'line-clamp-3  text-sm md:mt-2 md:line-clamp-2 md:text-base',
                                ]"
                            >
                                {{ el.title }}
                            </h2>
                        </a>
                        <a :href="el.url">
                            <p
                                v-if="idx !== 0"
                                class="text-xs font-light text-plus-gray-200 md:hidden"
                            >
                                {{ el.publicationdate }}
                            </p>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
